import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import BhawarLogo from "@/components/BhawarLogo";
import SamsungLogo from "@/components/SamsungLogo";

function formatErr(err) {
  if (!err) return "Something went wrong. Please try again.";
  if (typeof err === "string") return err;
  const detail = err.response?.data?.detail || err.response?.data?.message || err.message;
  if (detail == null) {
    if (err.response?.status) return `Request failed with status ${err.response.status}`;
    return "Network error or server unreachable. Please check your connection.";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return String(detail.msg);
  return String(detail);
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrorMsg("");
    try {
      const { data } = await api.post("/auth/login", { email, password }, { silent401: true });
      if (data?.access_token) {
        setToken(data.access_token);
      }
      setUser(data.user);
      navigate("/", { replace: true });
    } catch (err) {
      const msg = formatErr(err);
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-50 flex flex-col"
      style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
    >
      {/* Top brand bar */}
      <header className="w-full px-6 sm:px-10 py-6 flex items-center justify-between border-b border-slate-200 bg-white">
        <BhawarLogo height={64} tone="dark" data-testid="login-bhawar-logo" />
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="hidden sm:inline text-[10px] uppercase tracking-[0.24em] text-slate-500">
            Authorised Distributor
          </span>
          <SamsungLogo size="md" tone="dark" />
        </div>
      </header>

      {/* Sign-in card */}
      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h1
            className="text-2xl sm:text-3xl font-medium tracking-tight text-slate-900"
            style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}
          >
            Sign in
          </h1>
          <p className="mt-2 text-sm text-slate-600">Use the credentials issued to you.</p>

          {errorMsg && (
            <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-md text-xs text-rose-700 font-medium animate-in fade-in" data-testid="login-error-banner">
              {errorMsg}
            </div>
          )}

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-11"
                autoComplete="username"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-11"
                autoComplete="current-password"
                data-testid="login-password"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit-btn"
            className="w-full mt-6 bg-[#0055FF] hover:bg-[#0044CC] text-white h-11"
          >
            {busy ? "Signing in..." : "Sign in"}
          </Button>
        </form>
      </main>

      <footer className="w-full text-center text-[11px] text-slate-400 py-4">
        &copy; {new Date().getFullYear()} Bhawar Sales Corporation
      </footer>
    </div>
  );
}
